/** /signup?session=<id> — the student takes the offer in the footnote (STORY-113).
 *
 * The key is minted here, on this device, and the teacher's system records the key rather
 * than minting an account. Two teachers who never sync therefore end up with one student.
 * With policy `request` the answer is `pending` and the student stays a guest until their
 * teacher approves, which this screen says in as many words.
 */
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useSurface } from "../platform/surface";
import {
  card,
  register,
  signupsOf,
  SignupError,
  type RegistrationStatus,
} from "../live/studentIdentity";
import { lastJoin, recallJoin } from "../live/studentSession";
import { product } from "../product";
import { Button, PasswordInput } from "../ui";

export function SignUp() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  useSurface("student");
  const { session } = useSearch({ strict: false }) as { session?: string };
  const sitting = useMemo(() => (session ? recallJoin(session) : lastJoin()), [session]);
  const sessionId = sitting?.session_id ?? null;
  const policy = sessionId ? signupsOf(sessionId) : "off";
  const mine = card();

  const [name, setName] = useState(mine?.display_name ?? "");
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<RegistrationStatus | null>(null);
  /** The service records an email as an UNVERIFIED claim and never merges on it; when another
   * key on that system claimed the same address it says so, and the student is told plainly
   * rather than left thinking their history has been joined up. */
  const [conflict, setConflict] = useState(false);

  async function submit() {
    if (!sitting || !sessionId) return;
    setBusy(true);
    setError(null);
    try {
      const r = await register(sessionId, sitting.token, {
        display_name: name.trim(),
        email: email.trim() || undefined,
        passphrase: pass || undefined,
      });
      setDone(r.status);
      setConflict(r.email_conflict);
    } catch (e) {
      const code = e instanceof SignupError ? e.code : "generic";
      setError(t(`signup.errors.${code}`, { defaultValue: t("signup.errors.generic") }));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="font-system flex min-h-dvh flex-col justify-center gap-6 p-6">
      <header className="mx-auto w-full max-w-md">
        <h1 className="text-2xl font-bold tracking-tight" translate="no">
          {product.name}
        </h1>
        <p className="mt-1 text-lg">{t("signup.title")}</p>
        <p className="mt-1 text-sm text-[color:var(--ink-2)]">{t("signup.lede")}</p>
      </header>

      {done && (
        <section className="mx-auto w-full max-w-md rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 shadow-sm" aria-live="polite">
          <h2 className="text-lg font-semibold">{done === "pending" ? t("signup.done.pendingTitle") : t("signup.done.activeTitle")}</h2>
          <p className="mt-1 text-sm text-[color:var(--ink-2)]">{done === "pending" ? t("signup.done.pending") : t("signup.done.active")}</p>
          {conflict && (
            <p className="mt-2 text-sm" role="alert">
              {t("signup.done.emailConflict")}
            </p>
          )}
          <div className="mt-4 flex flex-col gap-2">
            {sessionId && (
              <Button type="button" block onClick={() => void navigate({ to: "/s/$sessionId", params: { sessionId } })}>
                {t("signup.done.back")}
              </Button>
            )}
            {done === "active" && (
              <Button type="button" variant="secondary" block onClick={() => void navigate({ to: "/my/transfer" })}>
                {t("signup.done.transfer")}
              </Button>
            )}
          </div>
        </section>
      )}
      {!done && !sitting && (
        <p className="mx-auto w-full max-w-md rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 text-sm shadow-sm">{t("signup.errors.no_session")}</p>
      )}
      {!done && sitting && policy === "off" && (
        <p className="mx-auto w-full max-w-md rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 text-sm shadow-sm">{t("signup.errors.off")}</p>
      )}
      {!done && sitting && policy !== "off" && (
        <form
          className="mx-auto flex w-full max-w-md flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          {policy === "request" && (
            <p className="rounded-[var(--radius-control)] bg-[color:var(--surface)] p-3 text-sm shadow-sm" role="note">
              {t("signup.requestNote")}
            </p>
          )}
          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium">{t("signup.name")}</span>
            <input
              className="field min-h-[var(--control-h)] text-lg"
              name="name"
              autoComplete="name"
              maxLength={60}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium">{t("signup.email")}</span>
            <input
              className="field min-h-[var(--control-h)]"
              type="email"
              name="email"
              inputMode="email"
              autoComplete="email"
              spellCheck={false}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <span className="text-xs text-[color:var(--ink-2)]">{t("signup.emailHint")}</span>
          </label>
          {!mine && (
            <PasswordInput
              label={t("signup.pass")}
              hint={pass ? t("signup.passHint") : t("signup.passNone")}
              value={pass}
              onChange={setPass}
              autoComplete="new-password"
              minLength={8}
              name="passphrase"
            />
          )}
          {error && (
            <p role="alert" className="rounded-[var(--radius-control)] bg-[color:var(--surface)] p-3 text-sm shadow-sm" style={{ color: "var(--not-yet)" }}>
              {error}
            </p>
          )}
          <Button type="submit" block loading={busy} disabled={!name.trim()}>
            {policy === "request" ? t("signup.ctaRequest") : t("signup.cta")}
          </Button>
          <p className="text-xs text-[color:var(--ink-2)]">{t("signup.privacy")}</p>
          <p className="text-sm">
            <Link to="/my/transfer" className="inline-flex min-h-[var(--control-h)] items-center underline">
              {t("signup.haveKey")}
            </Link>
          </p>
        </form>
      )}
    </main>
  );
}
